/**
 * @fileOverview This file defines the `CreateRegionModal` component, which provides a modal dialog
 * for creating a new region with a name and optional notes.
 * @version 1.0.0
 * @since 1.0.0
 * @author Santiago Cano Herrera
 */

//#region Imports
import { useState } from 'react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
//#endregion

/**
 * Component for displaying a modal to create a new region.
 * @param {Object} props - The component props.
 * @param {boolean} props.showModal - Indicates whether the modal is open.
 * @param {function} props.setShowModal - Function to toggle the modal's visibility.
 * @param {function} props.refreshData - Function to refresh the regions data after creation.
 * @returns {JSX.Element} The `CreateRegionModal` component.
 */
export const CreateRegionModal = ({ showModal, setShowModal, refreshData }) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking loading state
  const [newRegion, setNewRegion] = useState({ name: '', notes: '' }); // State for new region data

  /**
   * Resets the fields in the form to their initial state.
   */
  const handleResetFields = () => {
    setNewRegion({ name: '', notes: '' });
  };

  /**
   * Closes the modal and resets the form fields.
   */
  const handleClose = () => {
    setShowModal(false);
    handleResetFields();
    refreshData(); // Refresh regions data
  };

  /**
   * Handles the form submission to create a new region.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}region/create`;

    try {
      const response = await customAxios.post(endpoint, newRegion, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 200) {
        getToast({ status: 'success', title: 'Región creada exitosamente' }); // Show success message
        handleClose(); // Close modal and reset fields
      }
    } catch (error) {
      console.error('Error creating region:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      }); // Show error message
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <Dialog open={showModal} maxWidth='md' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Nueva región
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <form id='new-region-form' onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled={isLoading}
                label='Nombre'
                placeholder='Nombre...'
                type='text'
                value={newRegion.name}
                onChange={(e) =>
                  setNewRegion((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
            </Grid>
            {/* Notes Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled={isLoading}
                label='Notas'
                placeholder='Notas...'
                type='text'
                value={newRegion.notes}
                onChange={(e) =>
                  setNewRegion((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          form='new-region-form'
          type='submit'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size='2rem' />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
